import "@/styles/globals.css";
import { TRPCProvider } from "@/trpc/vite-provider";
import { type PageContext } from "vike/types";
import { PageContextProvider } from "./usePageContext";
import { Toaster } from "@/components/ui/sonner";
import { ClientOnly } from "@/components/ui/ClientOnly";
import { VersionDisplay } from "@/components/VersionDisplay";
import { PhoneFrameBodyPadding } from "@/components/ui/PageInPhoneFrame/PhoneFrameBodyPadding";
import { I18nextProvider } from "react-i18next";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { translations } from "@/features/i18n/translations";
import { env } from "@/env";
import { PreviewProtection } from "@/components/layout/PreviewProtection";
import { PreviewAccessHandler } from "@/components/ui/PreviewAccessHandler";

type PageShellProps = {
  children: React.ReactNode;
  pageContext: PageContext;
};

// Create a fresh i18n instance for each page render to avoid caching issues
function createI18nInstance(locale: string) {
  const instance = i18n.createInstance();

  // Use and init separately to ensure proper initialization
  instance.use(initReactI18next);
  void instance.init({
    resources: translations,
    fallbackLng: "en",
    load: "languageOnly",
    supportedLngs: ["en", "ru", "pt"],
    interpolation: { escapeValue: false },
    lng: locale,
  });

  return instance;
}

export const PageShell = function ({ children, pageContext }: PageShellProps) {
  // Get locale from pathname
  const pathname = pageContext.urlPathname;

  let locale = "en"; // default
  if (pathname.startsWith("/en")) locale = "en";
  else if (pathname.startsWith("/ru")) locale = "ru";
  else if (pathname.startsWith("/pt")) locale = "pt";
  else {
    // For root path "/", use the build's default locale
    const buildDefaultLocale = env.VITE_DEFAULT_LOCALE;
    locale = buildDefaultLocale || "en";
  }

  // Create isolated i18next instance for this locale
  const i18nInstance = createI18nInstance(locale);

  return (
    <I18nextProvider i18n={i18nInstance}>
      <TRPCProvider>
        <PageContextProvider pageContext={pageContext}>
          <ClientOnly showLoader={false}>
            <PreviewAccessHandler />
          </ClientOnly>
          <PreviewProtection>{children}</PreviewProtection>
          <ClientOnly showLoader={false}>
            <Toaster />
            <VersionDisplay />
            <PhoneFrameBodyPadding />
          </ClientOnly>
        </PageContextProvider>
      </TRPCProvider>
    </I18nextProvider>
  );
};
